import * as THREE from 'three';
import { InfractionLog } from './InfractionLog';
import { VehicleTelemetry } from '../vehicle/VehiclePhysics';
import { TownBuilder } from '../environment/TownBuilder';
import { COLLISION_GROUP_PEDESTRIAN } from '../core/PhysicsWorld';

export interface TrafficManagerEvaluationAdapter {
  pedestrians?: Array<{ position: THREE.Vector3; isCrossing?: boolean }>;
  vehicles?: Array<{ position: THREE.Vector3; speedKmh: number; waypoints?: THREE.Vector3[]; currentWaypointIdx?: number }>;
  cyclists?: Array<{ position: THREE.Vector3; speedKmh: number; type?: string }>;
  bus?: { position: THREE.Vector3; speedKmh: number; isAtBusStop: boolean; blinkerState: string } | null;
}

export class RuleEvaluators {
  private lastStopSignStopTime = 0;
  private isAtStopSign = false;
  private lastEvalTime = -1;

  // 3-S-Blick routine tracking (general + directional)
  public lastInnenspiegelTime = -999;
  public lastAussenspiegelTime = -999;
  public lastAussenspiegelLeftTime = -999;
  public lastAussenspiegelRightTime = -999;
  public lastBlinkerTime = -999;
  public lastBlinkerLeftTime = -999;
  public lastBlinkerRightTime = -999;
  public lastSchulterblickTime = -999;
  public lastSchulterblickLeftTime = -999;
  public lastSchulterblickRightTime = -999;

  // Cooldowns to prevent duplicate infraction spam
  private lastInfractionTimes: Map<string, number> = new Map();
  private enteredOnGreenLights: Set<any> = new Set();
  private clearingIntersectionUntil: number = 0;

  private lastObstacleOvertakingWarnTime = -999;
  private lastAutobahnWarnTime = -999;
  private lastRechtsfahrgebotWarnTime = -999;
  private lastFahrradstrasseWarnTime = -999;
  private autobahnStoppedSince = -1;
  private leftLaneSince = -1;

  public pedestrians: Array<{ position: THREE.Vector3; isCrossing?: boolean }> = [];

  constructor(
    private infractionLog: InfractionLog,
    private town: TownBuilder,
    private trafficManager?: TrafficManagerEvaluationAdapter
  ) {}

  public setTrafficManager(tm: TrafficManagerEvaluationAdapter) {
    this.trafficManager = tm;
  }

  public setPedestrians(peds: Array<{ position: THREE.Vector3; isCrossing?: boolean }>) {
    this.pedestrians = peds;
  }

  public getPedestrians(): Array<{ position: THREE.Vector3; isCrossing?: boolean }> {
    if (this.trafficManager && this.trafficManager.pedestrians && this.trafficManager.pedestrians.length > 0) {
      return this.trafficManager.pedestrians;
    }
    return this.pedestrians;
  }

  public recordAction(
    action:
      | 'innenspiegel'
      | 'aussenspiegel'
      | 'aussenspiegel_left'
      | 'aussenspiegel_right'
      | 'blinker'
      | 'blinker_left'
      | 'blinker_right'
      | 'schulterblick_left'
      | 'schulterblick_right',
    time: number
  ) {
    if (action === 'innenspiegel') this.lastInnenspiegelTime = time;
    if (action === 'aussenspiegel') {
      this.lastAussenspiegelTime = time;
      this.lastAussenspiegelLeftTime = time;
      this.lastAussenspiegelRightTime = time;
    }
    if (action === 'aussenspiegel_left') {
      this.lastAussenspiegelTime = time;
      this.lastAussenspiegelLeftTime = time;
    }
    if (action === 'aussenspiegel_right') {
      this.lastAussenspiegelTime = time;
      this.lastAussenspiegelRightTime = time;
    }
    if (action === 'blinker') {
      this.lastBlinkerTime = time;
      this.lastBlinkerLeftTime = time;
      this.lastBlinkerRightTime = time;
    }
    if (action === 'blinker_left') {
      this.lastBlinkerTime = time;
      this.lastBlinkerLeftTime = time;
    }
    if (action === 'blinker_right') {
      this.lastBlinkerTime = time;
      this.lastBlinkerRightTime = time;
    }
    if (action === 'schulterblick_left') {
      this.lastSchulterblickTime = time;
      this.lastSchulterblickLeftTime = time;
    }
    if (action === 'schulterblick_right') {
      this.lastSchulterblickTime = time;
      this.lastSchulterblickRightTime = time;
    }
  }

  public evaluateAll(
    time: number,
    telemetry: VehicleTelemetry,
    blinkerState: string,
    shoulderCheck: string | null,
    mirrorLook: string | null
  ) {
    const dt = this.lastEvalTime >= 0 && time > this.lastEvalTime ? Math.min(0.5, time - this.lastEvalTime) : 0.016;
    this.lastEvalTime = time;

    if (mirrorLook === 'interior') this.lastInnenspiegelTime = time;
    if (mirrorLook === 'left') {
      this.lastAussenspiegelTime = time;
      this.lastAussenspiegelLeftTime = time;
    }
    if (mirrorLook === 'right') {
      this.lastAussenspiegelTime = time;
      this.lastAussenspiegelRightTime = time;
    }
    if (blinkerState !== 'off') {
      this.lastBlinkerTime = time;
      if (blinkerState === 'left') this.lastBlinkerLeftTime = time;
      if (blinkerState === 'right') this.lastBlinkerRightTime = time;
    }
    if (shoulderCheck !== null) {
      this.lastSchulterblickTime = time;
      if (shoulderCheck === 'left') this.lastSchulterblickLeftTime = time;
      if (shoulderCheck === 'right') this.lastSchulterblickRightTime = time;
    }

    const pos = new THREE.Vector3(telemetry.position.x, telemetry.position.y, telemetry.position.z);

    this.checkSeatbeltAndPreDrive(time, telemetry, pos);
    this.checkSpeedLimits(time, telemetry, pos);
    this.checkStopSigns(time, dt, telemetry, pos);
    this.checkTrafficLights(time, telemetry, pos);
    this.checkZebraCrossings(time, telemetry, pos);
    this.checkRoundaboutRules(time, telemetry, pos, blinkerState, shoulderCheck);
    this.checkContinuousLine(time, telemetry, pos);
    this.checkRechtsVorLinks(time, telemetry, pos);
    this.checkChildrenZone(time, telemetry, pos);
    this.checkNoOvertaking(time, telemetry, pos);
    this.checkObstacleOvertaking(time, telemetry, pos, blinkerState);
    this.checkAutobahnRules(time, telemetry, pos, blinkerState);
    this.checkRechtsfahrgebot(time, telemetry, pos);
    this.checkFahrradstrassePriority(time, telemetry, pos);
    this.checkBusStopRules(time, telemetry, pos);
    this.checkCyclistOvertakingDistance(time, telemetry, pos);
    this.checkOneWayStreets(time, telemetry, pos);
  }

  private checkTrafficLights(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    if (!this.town || !this.town.trafficLights) return;

    // If driver legally entered the intersection on green/yellow, clearing the intersection (including left turns)
    // is fully permitted under StVO § 37 Abs. 2
    if (time < this.clearingIntersectionUntil) {
      return;
    }

    const carQuat = telemetry.quaternion
      ? new THREE.Quaternion(telemetry.quaternion.x, telemetry.quaternion.y, telemetry.quaternion.z, telemetry.quaternion.w)
      : new THREE.Quaternion();
    const carForward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);

    for (const tl of this.town.trafficLights) {
      const tlPos = tl.group.position;
      const dist = pos.distanceTo(tlPos);

      // Reset green entrance flag when leaving intersection zone
      if (dist > 16.0) {
        this.enteredOnGreenLights.delete(tl);
        continue;
      }

      // Directional check: ONLY lights facing towards the oncoming driver's travel direction apply!
      // A vehicle approaching a signal travels in tl.approachDirection (opposite to tl.direction).
      // NEVER evaluate lights the vehicle is driving away from or turning alongside.
      const approachDir = tl.approachDirection
        || (tl.direction ? tl.direction.clone().negate()
        : (tl.group && tl.group.rotation ? new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), tl.group.rotation.y)
        : carForward.clone()));
      const isApproachingSignal = carForward.dot(approachDir) > 0.55;

      if (!isApproachingSignal) {
        continue; // Ignore perpendicular cross-streets, opposing lanes, and departing traffic!
      }

      // If passing through on GREEN or YELLOW, grant intersection clearing immunity
      if (tl.phase === 'GREEN' || tl.phase === 'YELLOW') {
        if (dist < 9.5) {
          this.enteredOnGreenLights.add(tl);
          this.clearingIntersectionUntil = time + 7.5; // 7.5s clearance window to complete maneuver
        }
      }

      // In the intersection bounding area (< 6.5m)
      if (dist < 6.5 && Math.abs(telemetry.speedKmh) > 5.0) {
        const isRed = tl.phase === 'RED' || tl.phase === 'RED_YELLOW';
        if (isRed) {
          // If vehicle legally entered on green, clearing the intersection is legal under StVO § 37
          if (this.enteredOnGreenLights.has(tl)) {
            continue;
          }

          this.triggerInfraction(
            'red_light_violation',
            '§ 37 Abs. 2 StVO',
            'Rotlichtverstoß an Lichtzeichenanlage',
            'Einfahren in die Kreuzung bei Rotlicht. (Sofortiges Nichtbestehen der praktischen Fahrprüfung!)',
            'FATAL',
            time,
            pos
          );
        }
      }
    }
  }

  private checkZebraCrossings(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const zebraLocations = [
      new THREE.Vector3(0, 0, 42),
      new THREE.Vector3(0, 0, 78),
      new THREE.Vector3(30, 0, 60),
      new THREE.Vector3(-30, 0, 60),
    ];

    const peds = this.getPedestrians();

    for (const zebra of zebraLocations) {
      const dist = pos.distanceTo(zebra);
      if (dist < 6.0) {
        // Approaching Zebrastreifen at excessive speed (> 38 km/h without readiness to yield)
        if (telemetry.speedKmh > 38.0) {
          this.triggerInfraction(
            'zebra_too_fast',
            '§ 26 Abs. 1 StVO',
            'Nicht mäßige Geschwindigkeit am Fußgängerüberweg',
            'An Fußgängerüberwegen darf nur mit mäßiger Geschwindigkeit herangefahren werden.',
            'MAJOR',
            time,
            pos
          );
        }
      }

      // StVO § 26: Yielding to pedestrians on or stepping onto zebra crossings
      for (const ped of peds) {
        const pedDistToZebra = ped.position.distanceTo(zebra);
        if (pedDistToZebra < 4.5) {
          // Pedestrian is at/on zebra. If vehicle is in zebra approach/crossing zone (< 8.0m) and drives through without yielding (> 10 km/h)
          if (dist < 8.0 && Math.abs(telemetry.speedKmh) > 10.0) {
            this.triggerInfraction(
              'zebra_yield_violation',
              '§ 26 Abs. 1 StVO',
              'Fußgängervorrang am Zebrastreifen missachtet',
              'Fahrzeuge müssen Fußgängern, die den Fußgängerüberweg erkennbar benutzen wollen, das Überqueren ermöglichen (StVO § 26). Sofortiges Nichtbestehen der Fahrprüfung!',
              'FATAL',
              time,
              pos
            );
          }
        }
      }
    }
  }

  private checkChildrenZone(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // School / children zone near (35, 0, 64)
    const schoolZone = new THREE.Vector3(35, 0, 64);
    if (pos.distanceTo(schoolZone) < 22.0) {
      if (telemetry.speedKmh > 32.0) {
        this.triggerInfraction(
          'children_zone_speed',
          '§ 3 Abs. 2a StVO',
          'Nicht angepasste Geschwindigkeit im Bereich von Kindern',
          'Gegenüber Kindern und Hilfsbedürftigen muss die Geschwindigkeit deutlich verringert werden.',
          'MAJOR',
          time,
          pos
        );
      }
    }
  }

  private checkNoOvertaking(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // No-overtaking zone along boulevard (z between 25 and 45)
    if (Math.abs(pos.z - 35) < 12.0 && Math.abs(pos.x) < 4.0) {
      const isCrossingCenter = (pos.z > 0 && pos.x > 0.5) || (pos.z < 0 && pos.x < -0.5);
      if (isCrossingCenter && Math.abs(telemetry.speedKmh) > 10.0) {
        this.triggerInfraction(
          'ueberholverbot_violation',
          '§ 5 Abs. 3 StVO (Zeichen 276)',
          'Überholen im Überholverbot',
          'Im Bereich des Zeichens 276 darf nicht überholt werden.',
          'FATAL',
          time,
          pos
        );
      }
    }
  }

  private checkSeatbeltAndPreDrive(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // 1. Seatbelt check: Must be fastened while actively driving in gear (> 8 km/h, engine running)
    const isDrivingInGear = telemetry.engineRunning && telemetry.gear !== 'P' && telemetry.gear !== 'N';
    if (isDrivingInGear && Math.abs(telemetry.speedKmh) > 8.0 && !telemetry.seatbeltFastened) {
      this.triggerInfraction(
        'seatbelt_unfastened',
        '§ 21a Abs. 1 StVO',
        'Fahren ohne angelegten Sicherheitsgurt',
        'Vorgeschriebene Sicherheitsgurte müssen während der Fahrt angelegt sein.',
        'FATAL',
        time,
        pos
      );
    }

    // 2. Handbrake check: Trying to accelerate firmly while handbrake is latched and engine running
    if (telemetry.engineRunning && telemetry.handbrake && telemetry.throttle > 0.4 && Math.abs(telemetry.speedKmh) < 4.0) {
      this.triggerInfraction(
        'handbrake_engaged_drive',
        '§ 23 StVO',
        'Anfahrversuch mit angezogener Handbremse',
        'Vor dem Anfahren muss die Feststellbremse (Handbremse) vollständig gelöst werden.',
        'MINOR',
        time,
        pos
      );
    }
  }

  public checkCollision(
    time: number,
    collision: {
      impactSpeedKmh: number;
      impactIntensity: number;
      targetBody?: any;
      playerSpeedKmh?: number;
      isNpcFault?: boolean;
    },
    pos: THREE.Vector3
  ) {
    const isPedestrian = collision.targetBody && collision.targetBody.collisionFilterGroup === COLLISION_GROUP_PEDESTRIAN;

    if (isPedestrian) {
      if (collision.isNpcFault) return;

      this.triggerInfraction(
        'pedestrian_collision',
        '§ 1 Abs. 2 / § 26 StVO',
        'Zusammenstoß mit Fußgänger / Radfahrer',
        'Kollision mit einem schutzbedürftigen Verkehrsteilnehmer (StVO § 1 Abs. 2 / § 26). Gefährdung von Menschenleben!',
        'FATAL',
        time,
        pos
      );
      return;
    }

    // StVO Fault Attribution for traffic/obstacle collisions:
    // When student is stationary (< 3 km/h) or struck from behind by traffic (isNpcFault),
    // do NOT penalize the student! 0 points deducted, exam remains 100 points.
    if (collision.isNpcFault || (collision.playerSpeedKmh !== undefined && collision.playerSpeedKmh < 3.0)) {
      return;
    }

    const isHeavyCrash = collision.impactSpeedKmh > 12.0 || collision.impactIntensity > 0.35;
    const severity = isHeavyCrash ? 'FATAL' : 'MAJOR';

    this.triggerInfraction(
      'vehicle_collision',
      '§ 1 Abs. 2 / § 34 StVO',
      isHeavyCrash ? 'Verkehrsunfall durch Zusammenstoß' : 'Kollision / Sachbeschädigung mit Hindernis',
      `Fahrzeugkollision mit ${collision.impactSpeedKmh} km/h Aufprallgeschwindigkeit. Unverzügliches Anhalten und Absichern vorgeschrieben!`,
      severity,
      time,
      pos
    );
  }

  private triggerInfraction(key: string, code: string, title: string, desc: string, severity: 'MINOR' | 'MAJOR' | 'FATAL', time: number, pos: THREE.Vector3) {
    const lastTime = this.lastInfractionTimes.get(key) || -999;
    if (time - lastTime < 6.0) return; // 6s debounce
    this.lastInfractionTimes.set(key, time);

    return this.infractionLog.log(code, title, desc, severity, time, { x: pos.x, y: pos.y, z: pos.z });
  }

  private checkSpeedLimits(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const allowedLimit = this.town.roadNetwork.getSpeedLimitAt(pos);
    const currentSpeed = telemetry.speedKmh;

    if (currentSpeed > allowedLimit + 12) {
      this.triggerInfraction(
        'speed_fatal',
        '§ 3 StVO',
        'Erhebliche Geschwindigkeitsüberschreitung',
        `Sie fuhren ${Math.round(currentSpeed)} km/h bei erlaubten ${allowedLimit} km/h (über 10 km/h zu schnell).`,
        'FATAL',
        time,
        pos
      );
    } else if (currentSpeed > allowedLimit + 5) {
      this.triggerInfraction(
        'speed_major',
        '§ 3 StVO',
        'Geschwindigkeitsüberschreitung',
        `Sie fuhren ${Math.round(currentSpeed)} km/h bei erlaubten ${allowedLimit} km/h.`,
        'MAJOR',
        time,
        pos
      );
    }
  }

  private checkStopSigns(time: number, dt: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // Check near Stop signs at genuine T-junction entries connecting residential cross-streets to the Outer Ring road
    const stopLocations = [
      { pos: new THREE.Vector3(53.5, 0, 114), approachDir: new THREE.Vector3(0, 0, 1) },  // Heading North into Ring North
      { pos: new THREE.Vector3(-53.5, 0, -114), approachDir: new THREE.Vector3(0, 0, -1) }, // Heading South into Ring South
    ];

    const carQuat = telemetry.quaternion
      ? new THREE.Quaternion(telemetry.quaternion.x, telemetry.quaternion.y, telemetry.quaternion.z, telemetry.quaternion.w)
      : new THREE.Quaternion();
    const carForward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);

    for (const stopLoc of stopLocations) {
      if (carForward.dot(stopLoc.approachDir) < 0.55) {
        continue; // Only evaluate vehicles approaching the stop sign head-on
      }

      const dist = pos.distanceTo(stopLoc.pos);
      if (dist < 4.5) {
        if (!this.isAtStopSign) {
          this.isAtStopSign = true;
          this.lastStopSignStopTime = 0;
        }

        if (Math.abs(telemetry.speedKmh) < 0.1) {
          this.lastStopSignStopTime += dt;
        }
      } else if (this.isAtStopSign && dist >= 4.5 && dist < 10) {
        // Vehicle passed the stop sign area! Check if it stopped for >= 1.8s (~2-3s stop)
        if (this.lastStopSignStopTime < 1.8) {
          this.triggerInfraction(
            'stop_sign_fail',
            '§ 206 StVO',
            'Stoppschild überrollt / Haltelinie missachtet',
            'Fahrzeug kam am Stoppschild nicht mindestens 3 Sekunden zum vollständigen Stillstand.',
            'FATAL',
            time,
            pos
          );
        }
        this.isAtStopSign = false;
      }
    }
  }

  private checkRoundaboutRules(
    time: number,
    telemetry: VehicleTelemetry,
    pos: THREE.Vector3,
    blinkerState: string,
    shoulderCheck: string | null
  ) {
    if (!this.town || !this.town.roundabout) return;
    const isInside = this.town.roundabout.isInsideRoundabout(pos);
    const distToCenter = Math.hypot(pos.x, pos.z);

    // 1. Entering Kreisverkehr (Distance ~18m-21m)
    if (distToCenter >= 17.5 && distToCenter <= 21.0) {
      if (blinkerState === 'left' || blinkerState === 'right') {
        this.triggerInfraction(
          'kreisel_entry_blinker',
          '§ 8 Abs. 1a StVO',
          'Unzulässiges Blinken bei Einfahrt in den Kreisverkehr',
          'Das Betätigen des Fahrtrichtungsanzeigers bei der Einfahrt in den Kreisverkehr ist nach StVO verboten!',
          'MAJOR',
          time,
          pos
        );
      }
    }

    // 2. Exiting Kreisverkehr
    if (isInside && distToCenter >= 17.0 && telemetry.speedKmh > 8) {
      // If turning out of circle without right blinker
      if (blinkerState !== 'right' && time - this.lastBlinkerTime > 4.0) {
        this.triggerInfraction(
          'kreisel_exit_no_blinker',
          '§ 8 Abs. 1a StVO',
          'Fehlender Fahrtrichtungsanzeiger beim Verlassen des Kreisverkehr',
          'Die Absicht, den Kreisverkehr zu verlassen, muss durch rechtzeitiges Blinken nach rechts angezeigt werden.',
          'MAJOR',
          time,
          pos
        );
      } else if (
        distToCenter >= 18.2 &&
        distToCenter <= 20.2 &&
        shoulderCheck !== 'right' &&
        time - this.lastSchulterblickRightTime > 6.0 &&
        this.trafficManager?.cyclists &&
        this.trafficManager.cyclists.some((c) => c.position.distanceTo(pos) < 16.0)
      ) {
        this.triggerInfraction(
          'kreisel_exit_no_schulterblick',
          '§ 9 Abs. 1 StVO',
          'Fehlender Schulterblick beim Ausfahren über Radweg',
          'Beim Verlassen des Kreisverkehrs muss der Vorrang von Radfahrern und Fußgängern durch Schulterblick nach rechts abgesichert werden!',
          'MINOR',
          time,
          pos
        );
      }
    }
  }

  private checkContinuousLine(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // If inside marked Zeichen 276 zone, let checkNoOvertaking handle it
    if (Math.abs(pos.z - 35) < 12.0) return;

    // Intersections at z=60 and z=-60 are open junction areas with no continuous center line
    if (Math.abs(pos.z - 60) < 12.0 || Math.abs(pos.z + 60) < 12.0) return;

    // Check if player crossed into opposing lane on main road
    if (Math.abs(pos.x) < 4.0 && Math.abs(pos.z) > 25 && Math.abs(pos.z) < 110) {
      // Solid continuous line (Zeichen 295) is at center x = 0
      const isCrossingCenter = (pos.z > 0 && pos.x > 0.5) || (pos.z < 0 && pos.x < -0.5);
      if (isCrossingCenter) {
        this.triggerInfraction(
          'continuous_line_crossing',
          '§ 41 Abs. 1 StVO (Zeichen 295)',
          'Überfahren der durchgezogenen Linie',
          'Die durchgezogene Mittellinie darf weder überfahren noch berührt werden! (Gefährdung des Gegenverkehrs)',
          'FATAL',
          time,
          pos
        );
      }
    }
  }

  private checkRechtsVorLinks(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // Unmarked intersections in 30 zone: (50, 60), (-50, 60), (50, -60), (-50, -60)
    const intersections = [
      new THREE.Vector3(50, 0, 60),
      new THREE.Vector3(-50, 0, 60),
      new THREE.Vector3(50, 0, -60),
      new THREE.Vector3(-50, 0, -60),
    ];

    const carQuat = telemetry.quaternion
      ? new THREE.Quaternion(telemetry.quaternion.x, telemetry.quaternion.y, telemetry.quaternion.z, telemetry.quaternion.w)
      : new THREE.Quaternion();
    const carForward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat).normalize();
    // In Three.js coordinate system (+Z forward, +Y up), right-hand vector is (-forward.z, 0, forward.x)
    const carRight = new THREE.Vector3(-carForward.z, 0, carForward.x).normalize();

    for (const inter of intersections) {
      const dist = pos.distanceTo(inter);
      if (dist < 9.0) {
        // Approaching unmarked junction: must reduce speed to <= 26 km/h
        if (telemetry.speedKmh > 26) {
          this.triggerInfraction(
            'rvl_too_fast',
            '§ 8 StVO',
            'Nicht bremsbereit an Rechts-vor-Links-Kreuzung',
            'An unbeschilderten Kreuzungen im Wohngebiet muss bremsbereit und mit mäßiger Geschwindigkeit herangefahren werden.',
            'FATAL',
            time,
            pos
          );
        }

        // Real cross-traffic priority conflict check:
        // If student drives into intersection (dist < 7.5 && speed > 8 km/h) while an AI vehicle is approaching from the right within 14m
        if (dist < 7.5 && telemetry.speedKmh > 8.0 && this.trafficManager?.vehicles) {
          for (const ai of this.trafficManager.vehicles) {
            const aiDistToInter = ai.position.distanceTo(inter);
            if (aiDistToInter < 14.0 && aiDistToInter > 1.5 && ai.speedKmh > 4.0) {
              const toAi = ai.position.clone().sub(pos);
              const rightProj = toAi.dot(carRight);
              const fwdProj = toAi.dot(carForward);
              // AI car is positioned to the student's right side and ahead/entering the junction
              if (rightProj > 2.5 && fwdProj > -2.0 && fwdProj < 14.0) {
                this.triggerInfraction(
                  'rvl_priority_violation',
                  '§ 8 Abs. 1 StVO',
                  'Vorfahrt (Rechts-vor-Links) missachtet',
                  'Sie haben einem von rechts kommenden Fahrzeug an einer unbeschilderten Kreuzung nicht die Vorfahrt gewährt! (Sofortiges Nichtbestehen)',
                  'FATAL',
                  time,
                  pos
                );
                break;
              }
            }
          }
        }
      }
    }
  }

  public evaluateTurnRoutine(time: number, direction?: 'left' | 'right'): { valid: boolean; missing: string } {
    const hasInnenspiegel = time - this.lastInnenspiegelTime < 7.0;
    const hasAussenspiegel = direction === 'left'
      ? time - this.lastAussenspiegelLeftTime < 7.0
      : direction === 'right'
        ? time - this.lastAussenspiegelRightTime < 7.0
        : time - this.lastAussenspiegelTime < 7.0;
    const hasBlinker = direction === 'left'
      ? time - this.lastBlinkerLeftTime < 6.0
      : direction === 'right'
        ? time - this.lastBlinkerRightTime < 6.0
        : time - this.lastBlinkerTime < 6.0;
    const hasSchulterblick = direction === 'left'
      ? time - this.lastSchulterblickLeftTime < 5.0
      : direction === 'right'
        ? time - this.lastSchulterblickRightTime < 5.0
        : time - this.lastSchulterblickTime < 5.0;

    if (!hasInnenspiegel) return { valid: false, missing: 'Innenspiegel vor Richtungswechsel' };
    if (!hasAussenspiegel) return { valid: false, missing: direction === 'right' ? 'Rechter Außenspiegel' : direction === 'left' ? 'Linker Außenspiegel' : 'Außenspiegel' };
    if (!hasBlinker) return { valid: false, missing: 'Rechtzeitiges Blinken (30m vor Abbiegen)' };
    if (!hasSchulterblick) return { valid: false, missing: 'Schulterblick (Toter Winkel!)' };

    return { valid: true, missing: '' };
  }

  /**
   * Evaluates full 3-S-Blick presence AND chronological order:
   * 1. Innenspiegel -> 2. Außenspiegel -> 3. Blinker -> 4. Schulterblick
   */
  public evaluateManeuverObservation(
    time: number,
    direction: 'left' | 'right'
  ): { valid: boolean; missing: string; ordered: boolean } {
    const base = this.evaluateTurnRoutine(time, direction);
    if (!base.valid) {
      return { valid: false, missing: base.missing, ordered: false };
    }

    const tInnen = this.lastInnenspiegelTime;
    const tAussen = direction === 'left' ? this.lastAussenspiegelLeftTime : this.lastAussenspiegelRightTime;
    const tBlinker = direction === 'left' ? this.lastBlinkerLeftTime : this.lastBlinkerRightTime;
    const tSchulter = direction === 'left' ? this.lastSchulterblickLeftTime : this.lastSchulterblickRightTime;

    const ordered = tInnen <= tAussen + 0.5 && tAussen <= tBlinker + 1.0 && tSchulter >= tBlinker - 0.5;
    if (!ordered) {
      return {
        valid: false,
        missing: 'Reihenfolge fehlerhaft (Pflicht: Innenspiegel → Außenspiegel → Blinker → Schulterblick)',
        ordered: false,
      };
    }

    return { valid: true, missing: '', ordered: true };
  }

  private checkObstacleOvertaking(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3, blinkerState: string) {
    if (!this.town || !this.town.parkedCarPositions || this.town.parkedCarPositions.length === 0) return;
    const cooldown = 4.0;
    if (time - this.lastObstacleOvertakingWarnTime < cooldown) return;

    for (const carPos of this.town.parkedCarPositions) {
      const dist = pos.distanceTo(carPos);
      if (dist < 15.0 && dist > 2.0 && telemetry.speedKmh > 5.0) {
        if (blinkerState !== 'left') {
          this.lastObstacleOvertakingWarnTime = time;
          this.triggerInfraction(
            'engstelle_blinker',
            '§ 6 StVO',
            'Vorbeifahren an Hindernissen',
            'Vorbeifahren an parkendem Fahrzeug ohne rechtzeitiges Blinken nach links!',
            'MINOR',
            time,
            pos
          );
          return;
        }
      }
    }
  }

  private checkAutobahnRules(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3, blinkerState: string) {
    const currentLimit = this.town.roadNetwork.getSpeedLimitAt(pos);
    const isAutobahn = currentLimit === 130;
    if (!isAutobahn) {
      this.autobahnStoppedSince = -1;
      return;
    }

    const cooldown = 5.0;

    // Rule A: Minimum speed / stopping on Autobahn (§ 18 Abs. 8 StVO — Halteverbot)
    if (telemetry.speedKmh < 1.0) {
      if (this.autobahnStoppedSince < 0) {
        this.autobahnStoppedSince = time;
      } else if (time - this.autobahnStoppedSince >= 3.0) {
        if (time - this.lastAutobahnWarnTime >= cooldown) {
          this.lastAutobahnWarnTime = time;
          this.triggerInfraction(
            'ab_halten',
            '§ 18 Abs. 8 StVO',
            'Halten auf der Autobahn',
            'Unzulässiges Halten auf der Autobahnfahrbahn! Lebensgefährlich!',
            'FATAL',
            time,
            pos
          );
        }
        return;
      }
    } else {
      this.autobahnStoppedSince = -1;
    }

    // Rule B: Reversing on Autobahn (§ 18 Abs. 7 StVO — Wendeverbot / Rückwärtsfahren)
    if ((telemetry.gear === 'R' || telemetry.isReversing) && telemetry.speedKmh > 1.0) {
      if (time - this.lastAutobahnWarnTime >= cooldown) {
        this.lastAutobahnWarnTime = time;
        this.triggerInfraction(
          'ab_rueckwaerts',
          '§ 18 Abs. 7 StVO',
          'Rückwärtsfahren auf der Autobahn',
          'Rückwärtsfahren auf der Autobahn ist strengstens verboten!',
          'FATAL',
          time,
          pos
        );
      }
      return;
    }
  }

  private checkRechtsfahrgebot(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const currentLimit = this.town.roadNetwork.getSpeedLimitAt(pos);
    const isHighwayOrMultiLane = currentLimit === 130 || currentLimit === 100;
    if (!isHighwayOrMultiLane) {
      this.leftLaneSince = -1;
      return;
    }

    const cooldown = 6.0;

    // Check if driving in left lane
    const isLeftLane = (
      (Math.abs(pos.z) > 570 && Math.abs(pos.z) < 578) ||
      (Math.abs(pos.x) > 570 && Math.abs(pos.x) < 578)
    );

    if (isLeftLane && telemetry.speedKmh > 60 && telemetry.speedKmh < 120) {
      if (this.leftLaneSince < 0) {
        this.leftLaneSince = time;
      } else if (time - this.leftLaneSince > 8.0) {
        if (time - this.lastRechtsfahrgebotWarnTime >= cooldown) {
          this.lastRechtsfahrgebotWarnTime = time;
          this.triggerInfraction(
            'rechtsfahrgebot',
            '§ 2 Abs. 2 StVO',
            'Rechtsfahrgebot missachtet',
            'Verstoß gegen das Rechtsfahrgebot: Linke Spur dauerhaft belegt ohne Überholabsicht.',
            'MINOR',
            time,
            pos
          );
        }
      }
    } else {
      this.leftLaneSince = -1;
    }
  }

  private checkFahrradstrassePriority(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const inFahrradstrasse = Math.abs(pos.z - 30) < 4.0 && Math.abs(pos.x) < 116;
    if (!inFahrradstrasse) return;

    const cooldown = 4.0;
    if (time - this.lastFahrradstrasseWarnTime < cooldown) return;

    if (telemetry.speedKmh > 33.0) {
      this.lastFahrradstrasseWarnTime = time;
      this.triggerInfraction(
        'fahrrad_speed',
        '§ 45 StVO',
        'Fahrradstraße Geschwindigkeitsüberschreitung',
        'Geschwindigkeitsüberschreitung in Fahrradstraße! Höchstgeschwindigkeit 30 km/h.',
        'MAJOR',
        time,
        pos
      );
    }
  }

  private checkBusStopRules(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const bus = this.trafficManager?.bus;
    if (!bus || !bus.isAtBusStop) return;

    const dist2D = Math.hypot(pos.x - bus.position.x, pos.z - bus.position.z);
    const lateralDist = Math.abs(pos.x - bus.position.x);

    // Case A: Bus stopped at Haltestelle with hazard lights active (§ 20 Abs. 4 StVO -> Schrittgeschwindigkeit max 7-10 km/h)
    if (bus.blinkerState === 'hazard' && dist2D < 10.0 && lateralDist < 6.0) {
      if (Math.abs(telemetry.speedKmh) > 10.0) {
        this.triggerInfraction(
          'bus_hazard_speed',
          '§ 20 Abs. 4 StVO',
          'Nicht mit Schrittgeschwindigkeit am Linienbus vorbeigefahren',
          'An Omnibussen des Linienverkehrs, die an Haltestellen halten und Warnblinklicht eingeschaltet haben, darf nur mit Schrittgeschwindigkeit (4–7 km/h) vorbeigefahren werden!',
          'MAJOR',
          time,
          pos
        );
      }
    }

    // Case B: Bus signaling left to pull out from bus stop bay (§ 20 Abs. 5 StVO -> Abfahren ermöglichen!)
    if (bus.blinkerState === 'left') {
      // Bus is on southbound side facing -Z; approaching traffic from behind has pos.z > bus.position.z - 2.0
      if (pos.z > bus.position.z - 2.0 && pos.z < bus.position.z + 12.0 && lateralDist < 4.5) {
        if (Math.abs(telemetry.speedKmh) > 12.0) {
          this.triggerInfraction(
            'bus_departure_yield',
            '§ 20 Abs. 5 StVO',
            'Abfahrendem Linienbus den Vorrang missachtet',
            'Omnibussen des Linienverkehrs muss das Abfahren von gekennzeichneten Haltestellen ermöglicht werden! Wenn nötig, müssen andere Fahrzeuge warten.',
            'FATAL',
            time,
            pos
          );
        }
      }
    }
  }

  private checkCyclistOvertakingDistance(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    const cyclists = this.trafficManager?.cyclists;
    if (!cyclists || cyclists.length === 0) return;

    for (const c of cyclists) {
      const dist2D = Math.hypot(pos.x - c.position.x, pos.z - c.position.z);
      // Car half-width ~0.91m + Cyclist half-width ~0.30m = 1.21m touching distance.
      // Passing closer than 2.35m center-to-center means < 1.14m lateral clearance (violating the 1.5m StVO § 5 Abs. 4 mandate).
      if (dist2D < 2.35 && telemetry.speedKmh > 12.0 && telemetry.speedKmh > c.speedKmh + 5.0) {
        this.triggerInfraction(
          'cyclist_overtake_clearance',
          '§ 5 Abs. 4 StVO',
          'Seitenabstand beim Überholen von Radfahrern unterschritten',
          'Beim Überholen von zu Fuß Gehenden, Radfahrenden und Elektrokleinstfahrzeugführenden beträgt der vorgeschriebene Mindestseitenabstand innerorts 1,5 m (außerorts 2,0 m)!',
          'MAJOR',
          time,
          pos
        );
        return;
      }
    }
  }

  private checkOneWayStreets(time: number, telemetry: VehicleTelemetry, pos: THREE.Vector3) {
    // Segments res_30_cross_e_s (x = 50, z in [-114, -66]) and res_30_cross_w_s (x = -50, z in [-114, -66])
    // are northbound one-way streets (+Z) marked with Zeichen 220.
    const onOneWayCorridor =
      (Math.abs(pos.x - 50.0) < 3.2 || Math.abs(pos.x + 50.0) < 3.2) &&
      pos.z > -112.0 &&
      pos.z < -66.0;

    if (!onOneWayCorridor || telemetry.speedKmh < 8.0) return;

    const carQuat = telemetry.quaternion
      ? new THREE.Quaternion(telemetry.quaternion.x, telemetry.quaternion.y, telemetry.quaternion.z, telemetry.quaternion.w)
      : new THREE.Quaternion();
    const carForward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);

    // Driving southbound (-Z) against the one-way direction
    if (carForward.z < -0.6) {
      this.triggerInfraction(
        'oneway_wrong_way',
        '§ 41 StVO (Zeichen 220 / 267)',
        'Fahren entgegen der Einbahnstraße (Geisterfahrer)',
        'Sie befahren eine Einbahnstraße entgegen der vorgeschriebenen Fahrtrichtung! Sofortiges Nichtbestehen der Fahrprüfung.',
        'FATAL',
        time,
        pos
      );
    }
  }
}
